import { getTenantConfig } from '../../config/tenantConfig';
import httpClient from './httpClient';

/**
 * Unknown Faces API Service
 * Handles operations for unknown faces and SOI promotion
 * Uses httpClient for automatic 401 handling and token refresh
 */

/**
 * Promote unknown face to Subject of Interest (manual)
 * @param {string} unknownFaceId - Unknown face ID (e.g., "UNKNOWN-123")
 * @returns {Promise<Object>} Promotion result
 */
export async function promoteToSOI(unknownFaceId, promotionReason) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      console.error('Unknown Faces API endpoint not configured');
      throw new Error('Unknown Faces API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    const result = await httpClient.post(
      `${unknownFacesApi}/unknown-faces/${unknownFaceId}/promote-to-soi`,
      { promotion_reason: promotionReason }
    );
    return result;
  } catch (error) {
    console.error('Error promoting unknown face to SOI:', error);
    throw error;
  }
}

/**
 * Get all unknown faces for current tenant
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (UNKNOWN, SOI, IDENTIFIED)
 * @param {string} options.dateFrom - Filter by date from
 * @param {string} options.dateTo - Filter by date to
 * @returns {Promise<Array>} Array of unknown face objects
 */
export async function listUnknownFaces(options = {}) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const params = {};
    if (options.status) params.status = options.status;
    if (options.dateFrom) params.dateFrom = options.dateFrom;
    if (options.dateTo) params.dateTo = options.dateTo;

    const result = await httpClient.get(`${unknownFacesApi}/unknown-faces`, { params });
    return result.unknownFaces || [];
  } catch (error) {
    console.error('Error listing unknown faces:', error);
    throw error;
  }
}

/**
 * Get single unknown face by ID
 * @param {string} unknownFaceId - Unknown face ID
 * @returns {Promise<Object>} Unknown face object
 */
export async function getUnknownFace(unknownFaceId) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const result = await httpClient.get(`${unknownFacesApi}/unknown-faces/${unknownFaceId}`);
    return result.unknownFace || null;
  } catch (error) {
    console.error('Error fetching unknown face:', error);
    throw error;
  }
}

/**
 * Identify unknown face as known person (auto-generates person ID)
 * @param {string} unknownFaceId - Unknown face ID
 * @param {string} personId - Deprecated, ignored (person ID is auto-generated)
 * @param {string} dataset - Dataset to assign person to
 * @param {string} firstName - First name (required)
 * @param {string} lastName - Last name (optional)
 * @param {string} group - Group (optional)
 * @param {string} promotionReason - Reason for promotion (required)
 * @returns {Promise<Object>} Identification result
 */
export async function identifyAsPerson(unknownFaceId, personId, dataset, firstName, lastName, group, promotionReason) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const result = await httpClient.post(
      `${unknownFacesApi}/unknown-faces/${unknownFaceId}/identify-as-person`,
      {
        dataset: dataset,
        first_name: firstName,
        last_name: lastName,
        group: group,
        promotion_reason: promotionReason
      }
    );
    return result;
  } catch (error) {
    console.error('Error identifying unknown face as person:', error);
    throw error;
  }
}

/**
 * Identify SOI person as known person (SOI → Known promotion)
 * @param {string} soiPersonId - SOI person ID
 * @param {string} dataset - Dataset to assign person to
 * @param {string} firstName - First name (required)
 * @param {string} lastName - Last name (optional)
 * @param {string} group - Group (optional)
 * @param {string} promotionReason - Reason for promotion (required)
 * @returns {Promise<Object>} Identification result
 */
export async function identifySOIAsPerson(soiPersonId, dataset, firstName, lastName, group, promotionReason) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const result = await httpClient.post(
      `${unknownFacesApi}/soi-persons/${soiPersonId}/identify-as-person`,
      {
        dataset: dataset,
        first_name: firstName,
        last_name: lastName,
        group: group,
        promotion_reason: promotionReason
      }
    );
    return result;
  } catch (error) {
    console.error('Error identifying SOI person as known person:', error);
    throw error;
  }
}

/**
 * Demote known person to SOI (Known → SOI demotion)
 * @param {string} personId - Person ID
 * @param {string} interestReason - Reason for demotion (optional)
 * @returns {Promise<Object>} Demotion result
 */
export async function demotePersonToSOI(personId, interestReason) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const result = await httpClient.post(
      `${unknownFacesApi}/persons/${personId}/demote-to-soi`,
      {
        interest_reason: interestReason || 'Demoted from known person'
      }
    );
    return result;
  } catch (error) {
    console.error('Error demoting person to SOI:', error);
    throw error;
  }
}

/**
 * Demote SOI person to unknown (SOI → Unknown demotion)
 * @param {string} soiPersonId - SOI person ID
 * @returns {Promise<Object>} Demotion result
 */
export async function demoteSOIToUnknown(soiPersonId) {
  try {
    const config = getTenantConfig();
    const unknownFacesApi = config.api.unknownFaces;

    if (!unknownFacesApi) {
      throw new Error('Unknown Faces API not configured');
    }

    const result = await httpClient.post(
      `${unknownFacesApi}/soi-persons/${soiPersonId}/demote-to-unknown`
    );
    return result;
  } catch (error) {
    console.error('Error demoting SOI to unknown:', error);
    throw error;
  }
}
